// Note that this file contains classes for both lessons and activities

class Lesson {
	constructor(data, master_data) {
		if (empty(data)) data = {}

		// allow for different variants, which we will just label 'A', 'B', etc.
		sdp(this, data, 'lp_variant', 'A')

		sdp(this, data, 'lesson_id', 0)

		sdp(this, data, 'agency_sanctioned', false)
		sdp(this, data, 'home_collection_id', 0)
		sdp(this, data, 'publish_status', null)
		sdp(this, data, 'submitted_date', null)
		sdp(this, data, 'status_date', null)
		sdp(this, data, 'review_note', '')

		sdp(this, data, 'lesson_template_id', 0)
		sdp(this, data, 'lesson_title', '')
		sdp(this, data, 'human_code', '')	// e.g. "1.3" -- mapped to CASE humanCodingScheme

		// if the lesson is imported from a case CFItem, store the CFItem identifier here
		sdp(this, data, 'case_definition_identifier', '')

		// lesson status is empty, 'archived', or 'deleted'
		// but note that we don't currently load archived or deleted lessons to the client, so we don't really need to store this here
		sdp(this, data, 'lesson_status', '')

		sdp(this, data, 'lesson_date', '')	// format: 2022-03-24; see date_header() below for how to convert this to a date object
		sdp(this, data, 'lesson_date_end', '')	// format: 2022-03-24; see date_header() below for how to convert this to a date object

		sdp(this, data, 'course_code', '')
		sdp(this, data, 'lp_unit_id', 0)
		sdp(this, data, 'checked_out_for_edit', {user_id: null, timestamp: null})

		sdp(this, data, 'reviewers', [])
		sdp(this, data, 'grades', [])
		sdp(this, data, 'subjects', [])

		sdp(this, data, 'new_until_date', null)

		// temporary booleans for whether the lesson is fully loaded and whether or not things are showing
		// for lesson_fully_loaded, if we already have a key_uuid, use the lesson_fully_loaded value we calculated when the lesson was first created
		if (data.key_uuid) this.lesson_fully_loaded = data.lesson_fully_loaded
		// otherwise, if data.lesson_plan is specified, the lesson has been fully loaded
		else this.lesson_fully_loaded = !empty(data.lesson_plan)
		this.lesson_showing = false
		this.item_showing = false
		this.sparkl_showing = false
		this.resources_showing = false
		this.standards_showing = false
		this.student_description_showing = false
		this.activity_type = 'lesson'	// see DirectivesListItem

		// arbitrary uuid to use as the key in components; removed before saving
		this.key_uuid = U.new_uuid()

		this.lesson_plan = []

		// note value of data.lesson_master_id in case we need to convert from variant A to B
		const original_lesson_master_id = data.lesson_master_id

		// variant 'B': supports GPT suggestions, as well as associated sparkl activities
		// note that the instance can set allow_llm_lesson_conversion to 'always' to force conversion to variant B;
		// the instance must define 'legacy_x' schemes for each variant A lesson master
		if (this.lp_variant == 'B' || vapp.site_config.allow_llm_lesson_conversion == 'always') {
			master_data = window.variant_b_lesson_master_data
			
			// if we're converting from variant A (we will know this is so if its lesson_master_id value isn't 9)...
			let converting_from_variant_a = false
			if (data.lesson_master_id != 9) {
				this.lp_variant = 'B'
				converting_from_variant_a = true

				// find the scheme that goes with the original lesson's master; if not found, use legacy_5
				data.lp_component_scheme = 'legacy_' + data.lesson_master_id
				if (empty(master_data.lp_component_schemes[data.lp_component_scheme])) data.lp_component_scheme = 'legacy_5'
			}

			// get a valid scheme to use; if the data-specified scheme doesn't exist, use 'gradual_release'
			sdp(this, data, 'lp_component_scheme', 'gradual_release')
			if (empty(master_data.lp_component_schemes[this.lp_component_scheme])) this.lp_component_scheme = 'gradual_release'
			let scheme = master_data.lp_component_schemes[this.lp_component_scheme]

			// we'll store variant B lessons as lesson_master_id 9, though lesson_master_id isn't really relevant anymore
			this.lesson_master_id = '9'

			sdp(this, data, 'sparkl_activity_id', 0)

			sdp(this, data, 'lp_grade_level', '')
			
			// counters for GPT activity
			sdp(this, data, 'gpt_suggestion_requests', 0)
			sdp(this, data, 'gpt_suggestion_accepts', 0)
			sdp(this, data, 'gpt_input_tokens', 0)
			sdp(this, data, 'gpt_output_tokens', 0)

			// go through each component in the lesson's scheme and construct the the lesson's lesson_plan, ordering and converting things as needed...
			let debug = false
			let found = []
			let scheme_lc_uuids = []

			// this can be useful for debugging
			// console.warn(`lesson “${this.lesson_title}” / scheme “${scheme.label}”`)

			// for each group in the component_scheme
			for (let group of scheme.groups) {
				// for each component in this group
				for (let lc_uuid of group.group_components) {
					let mc = master_data.lesson_plan.find(x=>x.lc_uuid == lc_uuid)
					if (empty(mc)) {
						console.error(`Error: group '${group}' lesson_plan component '${lc_uuid}' not found in lp_component_scheme '${this.lp_component_scheme}'`)
						continue
					}

					// create this list of lc_uuids for use below if we need to convert
					scheme_lc_uuids.push(lc_uuid)

					///////////////////////////////////
					// if we're converting from a legacy (variant A) lesson, ...
					if (converting_from_variant_a) {
						// if this master component has a lc_legacy_component_index defined,
						// convert data.lesson_plan[lc_legacy_component_index] to the content for this lc_uuid
						if (!empty(data.lesson_plan) && !empty(mc.lc_legacy_component_index)) {
							let s = ''
							let component = data.lesson_plan[mc.lc_legacy_component_index]	// this should always exist
							for (let pi = 0; pi < component.lc_parts.length; ++pi) {
								let part = component.lc_parts[pi]

								// add a <hr> tag before and between parts
								if (component.lc_parts.length > 1) s += '<hr>'

								// add lcp_title if it exists; or add "Part X" if there is more than 1 part
								if (!empty(part.lcp_title)) {
									s += `<p><b>${part.lcp_title}</b></p>`
								} else if (component.lc_parts.length > 1) {
									s += `<p><b>Part ${pi+1}</b></p>`
								}

								s += part.lcp_teacher_content
							}

							this.lesson_plan.push(
								new Lesson_Component_B({
									lc_uuid: lc_uuid,
									lc_content: s,
								})
							)

							// when converting from variant A to B, we always have a place for the A component in B
							found[mc.lc_legacy_component_index] = true

						// otherwise add a copy of the component from the master lesson plan
						// (these won't be initially shown in the lesson viewer, but they may be shown [and edited] in the editor, 
						// and we need them to be set at the start for reactivity to work)
						} else {
							if (debug) console.warn(lc_uuid + ': not found in data; adding from MASTER')
							let comp = new Lesson_Component_B(mc)
							this.lesson_plan.push(comp)
						}

					///////////////////////////////////
					// the lesson was already configured as variant B, so we don't need to convert
					} else {
						// if this component is in the incoming lesson_plan, add it
						let i = data.lesson_plan?.findIndex(x=>x.lc_uuid == lc_uuid) 
						if (i > -1) {
							if (debug) console.warn(lc_uuid + ': adding from data')
							// note that we've found a place for this component in the designated scheme
							found[i] = true
							let comp = new Lesson_Component_B(data.lesson_plan[i])
							this.convert_legacy_list_components(mc, comp)
							this.lesson_plan.push(comp)

						// otherwise add a copy of the component from the master lesson plan
						// (these won't be initially shown in the lesson viewer, but they may be shown [and edited] in the editor, 
						// and we need them to be set at the start for reactivity to work)
						} else {
							if (debug) console.warn(lc_uuid + ': not found in data; adding from MASTER')
							let comp = new Lesson_Component_B(mc)
							this.lesson_plan.push(comp)
						}
					}
				}
			}

			// console.warn('found', found)

			// now, if we have any incoming, non-empty lp components that aren't part of this scheme, put them in somewhere...
			// this can happen when we convert an existing lesson from one scheme to another
			if (data.lesson_plan) for (let i = 0; i < data.lesson_plan.length; ++i) {
				if (!found[i]) {
					let component = data.lesson_plan[i]

					// skip if the component has no content
					if (empty(component.lc_content)) continue

					let convert_to_lc_uuid

					// look for a master component for the component's original lc_uuid
					let mc = master_data.lesson_plan.find(x=>x.lc_uuid == component.lc_uuid)
					if (!empty(mc)) {
						// if we have an lc_convertible_to list for the mc...
						if (!empty(mc.lc_convertible_to)) {
							// look for the first lc_uuid in this list that is a component in the lesson's scheme
							for (let lc_uuid of mc.lc_convertible_to) {
								if (scheme_lc_uuids.includes(lc_uuid)) {
									convert_to_lc_uuid = lc_uuid
									break
								}
							}
						}
					}

					// if we haven't found a convert_to_lc_uuid...
					if (empty(convert_to_lc_uuid)) {
						// use overview if it's part of the scheme
						if (scheme_lc_uuids.includes('overview')) {
							convert_to_lc_uuid = 'overview'

						// or use the first lc_uuid in the scheme
						} else {
							convert_to_lc_uuid = scheme_lc_uuids[0]
						}
					}

					// add the original component's lc_content to the convert_to_lc_uuid (which should definitely exist, based on code above)
					let convert_to_component = this.lesson_plan.find(x=>x.lc_uuid == convert_to_lc_uuid)
					if (empty(convert_to_component)) {
						console.error('couldn’t find convert_to_lc_uuid ' + convert_to_lc_uuid)
					} else {
						// add a separator if needed
						if (!empty(convert_to_component.lc_content)) {
							if (component.lc_content.search(/^<hr>/) == -1) {
								convert_to_component.lc_content += '<hr>'
							}
						}
						// then add to lc_content
						convert_to_component.lc_content += component.lc_content
						// and set lc_status to 'complete'
						convert_to_component.lc_status = 'complete'
					}
				}
			}

		///////////////////////////////////
		// this is for variant 'A' -- default (pre-ALBA) -- lessons that we're *NOT* converting to variant B
		} else {
			sdp(this, data, 'lesson_master_id', 0)

			// if we received master_data, use that for the structure of the lesson_plan
			if (master_data && master_data.lesson_plan) {
				for (let i = 0; i < master_data.lesson_plan.length; ++i) {
					// start with a shallow copy of the master's lesson_component, which should include lc_default_content (if provided in the master)
					let c = Object.assign({}, master_data.lesson_plan[i])

					// then splice in lc_parts from data if there
					if (data.lesson_plan && data.lesson_plan[i]) {
						c.lc_parts = data.lesson_plan[i].lc_parts
						c.lc_showing = data.lesson_plan[i].lc_showing	// preserve whether or not the component is showing
					} else {
						c.lc_parts = []
						// if we have lc_default_content for the master component, add it as the first part of the component
						if (!empty(c.lc_default_content)) {
							c.lc_parts.push(new Lesson_Component_Part({lcp_teacher_content:c.lc_default_content}))
						}
					}

					// create the component from this data
					this.lesson_plan.push(new Lesson_Component(c))

					// note that when we save, we'll pull out everything except lc_parts
				}

			// else we didn't receive master_data, so just use the data in data directly
			} else {
				if (!empty(data.lesson_plan)) {
					for (let c of data.lesson_plan) {
						this.lesson_plan.push(new Lesson_Component(c))
					}
				}
			}
		}

		sdp(this, data, 'student_description', '')

		// if lesson is restricted to signed-in users, this will be 1 (true)
		sdp(this, data, 'restricted', false)

		this.resources = []
		if (!empty(data.resources)) {
			for (let r of data.resources) {
				this.resources.push(new Resource(r))
			}
		}

		this.lessons = []
		if (!empty(data.lessons)) {
			for (let r of data.lessons) {
				this.lessons.push(new Lesson(r))
			}
		}

		// when we first load lessons from the server, we will get a resource_ids string that specifies the order of the resources (and ditto for lessons)
		if (!empty(data.resource_ids) && typeof(data.resource_ids) == 'string') {
			let order = data.resource_ids.split(',')
			let arr = []
			for (let resource_id of order) {
				let r = this.resources.find(x=>x.resource_id == resource_id)
				if (r) arr.push(r)
			}
			this.resources = arr
			// we don't need the resource_ids field to be saved
		}
		if (!empty(data.lesson_ids) && typeof(data.lesson_ids) == 'string') {
			let order = data.lesson_ids.split(',')
			let arr = []
			for (let lesson_id of order) {
				let r = this.lessons.find(x=>x.lesson_id == lesson_id)
				if (r) arr.push(r)
			}
			this.lessons = arr
			// we don't need the lesson_ids field to be saved
		}

		this.standards = []
		if (!empty(data.standards)) {
			for (let standard of data.standards) {
				this.add_standard(standard)
			}
		}

		// if we are converting from variant A and we have multiple standards, make all except the first one secondary
		if (original_lesson_master_id != this.lesson_master_id && this.standards.length > 1) {
			for (let i = 1; i < this.standards.length; ++i) {
				this.standards[i].is_secondary = true
			}
		}

		// case_resource_extensions only if we got it
		if (!empty(data.case_resource_extensions)) {
			this.case_resource_extensions = data.case_resource_extensions
		}

		sdp(this, data, 'creator_user_id', 0)
		sdp(this, data, 'creator_name', '')

		// convert dates from mysql to timestamp if necessary
		let tzo = new Date().getTimezoneOffset()*60*1000	// dates come in GMT; convert to local

		if (!isNaN(data.edited_date*1)) this.edited_date = data.edited_date*1
		else this.edited_date = (empty(data.edited_date)) ? 0 : (date.parse(data.edited_date, 'YYYY-MM-DD HH:mm:ss').getTime() - tzo) / 1000

		if (!isNaN(data.created_at*1)) this.created_at = data.created_at*1
		else this.created_at = (empty(data.created_at)) ? 0 : (date.parse(data.created_at, 'YYYY-MM-DD HH:mm:ss').getTime() - tzo) / 1000
	}

	copy_for_save(flag) {
		let o = $.extend(true, {}, this)

		delete o.key_uuid
		delete o.component
		delete o.lesson_fully_loaded
		delete o.editing
		delete o.lesson_showing
		delete o.item_showing
		delete o.sparkl_showing
		delete o.resources_showing
		delete o.standards_showing
		delete o.student_description_showing
		delete o.activity_type
		delete o.checked_out_for_edit
		delete o.reviewers
		delete o.grades
		delete o.subjects

		// remove full resources object; attach resource_ids as a comma-delimited string; ditto for lessons
		delete o.resources
		if (this.resources.length > 0) {
			o.resource_ids = ''
			for (let r of this.resources) {
				if (o.resource_ids != '') o.resource_ids += ','
				o.resource_ids += r.resource_id
			}
		}
		// if there are no resources left, have to clear the resource_ids field
		if (empty(o.resource_ids)) o.resource_ids = '*CLEAR*'

		delete o.lessons
		if (this.lessons.length > 0) {
			o.lesson_ids = ''
			for (let r of this.lessons) {
				if (o.lesson_ids != '') o.lesson_ids += ','
				o.lesson_ids += r.lesson_id
			}
		}
		// if there are no lessons left, have to clear the lesson_ids field
		if (empty(o.lesson_ids)) o.lesson_ids = '*CLEAR*'

		o.lesson_plan = [];
		for (let c of this.lesson_plan) {
			o.lesson_plan.push(c.copy_for_save(flag))
		}

		// get savable copies for standards
		o.standards = []
		for (let standard of this.standards) {
			o.standards.push(standard.copy_for_save())
		}

		if (this.publish_status && this.publish_status.hasOwnProperty('key')) {
			this.publish_status = this.publish_status.key
		}
		
		// we don't want to send empty strings or 0s in to the service for certain things
		if (o.edited_date == 0) delete o.edited_date
		if (o.created_at == 0) delete o.created_at
		if (empty(o.resource_ids)) delete o.resource_ids
		if (empty(o.lesson_ids)) delete o.lesson_ids
		return o
	}

	add_standard(standard) {
		// make sure we don't add the same standard twice
		if (this.standards.find(x=>x.identifier == standard.identifier)) {
			return
		}
		// for now at least we'll use the learning progression CASE_Item structure for standards; this is a bit simpler than the full CFItem structure
		this.standards.push(new CASE_Item(standard))
	}

	course_unit_header() {
		if (!this.course_code) return ''

		let lp = vapp.$store.state.all_courses.find(x=>x.course_code==this.course_code)
		if (!lp) lp = vapp.$store.state.sis_classes.find(x=>x.course_code == this.course_code)
		if (!lp) return this.course_code

		let s = lp.title

		if (this.lp_unit_id && lp.units) {
			let u = lp.units.find(x=>x.lp_unit_id == this.lp_unit_id)
			if (u && u.display_number) {
				s += ': <nobr>' + u.display_number + '</nobr>'
			}
		}

		let style = U.collection_color_style(lp)
		s = `<div class="k-lesson-course-unit-header-inner" style="border-color:${style['background-color']}; color:${style['background-color']}">${s}</div>`

		return s
	}

	date_color() {
		if (!this.lesson_date) return 'grey'
		let d = new Date(this.lesson_date + 'T00:00:00')
		// green if in the future (date is > now); otherwise amber
		return (d.getTime() > vapp.$store.state.now_date_obj.getTime()) ? 'teal' : 'amber'
	}

	date_header() {
		if (!this.lesson_date) return ''

		let d = new Date(this.lesson_date + 'T00:00:00')
		let ds = date.format(d, 'ddd M/D').replace(/(\d+\/\d+)/, '<span class="k-lesson-date-header-date">$1</span>')

		if (!empty(this.lesson_date_end)) {
			let d = new Date(this.lesson_date_end + 'T00:00:00')
			ds += ' – ' + date.format(d, 'ddd M/D').replace(/(\d+\/\d+)/, '<span class="k-lesson-date-header-date">$1</span>')
		}
		return `<div class="k-lesson-date-header k-lesson-date-header-${this.date_color()}">${ds}</div>`
	}

	// lessons cannot be assigned to different dates, but define these for convenience in places where we consider lessons and activities together (e.g. DirectivesGantt)
	due_date() { return '' }
	available_date() { return '' }
	has_multiple_available_dates() { return false }
	has_multiple_due_dates() { return false }

	// these four fns have analogous versions in resources.js
	// this is a link that opens this lesson in the context of this cureum instance
	standalone_cureum_link() { 
		return window.location.origin + '/lesson/' + this.lesson_id 
	}
	direct_resource_url() {
		// for lessons, you have to go through cureum
		return this.standalone_cureum_link()
	}
	title() { return this.lesson_title }
	icon() { return U.directive_type_icon('lesson') }	// use the directory_type fns for this
	type_label(capitalize) { 
		let s = U.directive_type_label('lesson') 
		if (capitalize) return s
		else return s.toLowerCase()
	}

	// return the html to insert into a froala-editable area (e.g. a lesson component) with a link to a lesson being used as a resource
	froala_resource_link_html() {
		let title = this.lesson_title.replace(/"/g, '\\"')
		// note nbsp's on either side, to make sure we can edit around the resource link
		return `&nbsp;<link class="k-lesson-component-resource-link fas ${this.icon()}" title="${title}" data-resource-link-id="${U.new_uuid()}" onclick="vapp.open_resource_link(\'${this.lesson_id}\',this)">&nbsp;`
	}

	available_to_students() {
		// show lessons iff they have a date and a student_description
		return (!empty(this.lesson_date) && !empty(this.student_description))
	}

	sort_date() {
		let s = this.lesson_date ? this.lesson_date : '9999-99-99'
		// lessons are sometimes sorted along with activities, which may have available_dates. Add a placeholder available_date to the lesson sort_date value, manipulating it so that for activities and lessons with the same dates, lessons will come first
		s += ' 0000-00-00'
		// s += ' 9999-99-98'
		return s
	}

	lesson_date_passed(now) {
		if (empty(now)) now = vapp.$store.state.now_date_string
		// the lesson date is passed if it has an lesson date and the lesson date is < now
		return this.lesson_date != '' && this.lesson_date < now
	}

	// this is implemented for both activities and lessons: we consider an item "old" if it has a date and its date is < 7 days prior to now
	item_is_old() {
		return this.lesson_date != '' && this.lesson_date < vapp.$store.state.old_threshold_date_string
	}

	// return true iff the lesson is visible by the given user role; if role not specified, use state role
	// note that callers of this fn should check themselves for whether or not the user is an editor for the collection, as collection editors can always see everything
	is_visible_by_user(role) {
		// if show_all_items_when_not_signed_in is 'yes', everyone gets to see everything
		if (vapp.site_config.show_all_items_when_not_signed_in == 'yes') return true

		if (empty(role)) role = vapp.$store.getters.role

		// for now at least, it's easy: staff and admins see lessons; students and parents don't
		if (role == 'student' || role == 'parent') return false
		return true
	}

	description_for_case_resource() {
		if (this.lp_variant == 'B') {
			// for lpc, send the lesson overview section as the description, if we have one
			let ovv = this.lesson_plan.find(x=>x.lc_uuid == 'overview')
			if (ovv && !empty(ovv.lc_content)) {
				return U.html_to_text(ovv.lc_content)
			}
		}
		// if we get to here, send a generic description
		return 'Template lesson plan'
	}

	full_text_for_case_resource() {
		let s = ''
		if (!empty(this.lesson_plan)) {
			for (let lc of this.lesson_plan) {
				// content structure for lpc lesson...
				if (this.lp_variant == 'B') {
					// here we have a series of components in the lesson_plan, each of which will have a single lc_content string (if it's filled in)
					s += U.html_to_text(lc.lc_content || '');
					s += ' ';

				// or original lesson...
				} else if (!empty(lc.lc_parts)) {
					// here we have a series of components in the lesson plan, each of which should have lc_parts; then each lc_part should have lcp_teacher_content
					for (let lcp of lc.lc_parts) {
						// this shouldn't happen for "real" lessons, but we put it in just in case
						if ($.isArray(lcp.lcp_teacher_content)) continue
						s += U.html_to_text(lcp.lcp_teacher_content || '');
						s += ' ';
					}
				}
			}
		}

		// note that we convert to plan text above, but we don't remove punctuation or upper case
		return s
	}

	reset_checked_out_for_edit() {
		if (this.lesson_id == 0) return;
		U.ajax('reset_checked_out_for_edit', {user_id: vapp.user_info.user_id, lesson_id: this.lesson_id})
	}

	convert_legacy_list_components(mc, comp) {
		// for 'list'-style elements, which we used to just code as "bare" lines separated by <br> tags, convert to ul/li's here (see add_suggestion in LessonEditorEnhanced)
		if (mc?.lc_text_format == 'list') {
			if (!empty(comp.lc_content) && comp.lc_content.search(/<(p|ul|li|div|table)\b/) == -1) {
				if (comp.lc_content.search(/<br>/) > -1) {
					let s  = ''
					let lines = comp.lc_content.split('<br>')
					for (let line of lines) s += `<ul><li>${line}</li></ul>`
					comp.lc_content = s
				}
			}
		}
	}

	// starting with a CASE cfitem, transfer data to the lesson class
	// if sot (Source Of Truth) == 'case', overwrite existing data from the lesson class item;
	// otherwise only use case data when lesson class item is unspecified
	case_to_lesson_properties(cfitem, sot) {
		let changed = false

		let update_prop_value = (o, prop, new_value, prop_prefix) => {
			// if sot (Source Of Truth) == 'case', always overwrite the prop value
			if (sot == 'case') {
				if (o[prop] != new_value) {
					console.warn(`changed lesson prop ${prop_prefix}${prop} (old, new)`)
					console.log(o[prop])
					console.log(new_value)

					o[prop] = new_value
					changed = true
				}

			} else {
				// if prop value already exists, don't overwrite
				if (!empty(o[prop])) {
					return

				// else set the prop value if changed
				} else if (o[prop] != new_value) {
					console.warn(`changed lesson prop ${prop_prefix}${prop} (old, new)`)
					console.log(o[prop])
					console.log(new_value)

					o[prop] = new_value
					changed = true
				}
			}
		}

		// case_definition_identifier always gets incoming identifier
		this.case_definition_identifier = cfitem.identifier

		// lesson title and human_code
		update_prop_value(this, 'lesson_title', cfitem.fullStatement, '')
		update_prop_value(this, 'human_code', cfitem.humanCodingScheme ?? '', '')

		// if we have extensions, fill in lesson components...
		if (!empty(cfitem.extensions)) {
			// go through each component in variant_b_lesson_master_data.lesson_plan
			// (which could include custom components for this cureum instance)
			for (let lc of window.variant_b_lesson_master_data.lesson_plan) {
				// if we have an lc_case_field for this component, add data for the component if specified in extensions
				if (!empty(lc.lc_case_field)) {
					let val = cfitem.extensions[lc.lc_case_field]
					if (!empty(val)) {
						// apply marked_latex to val
						let new_lc_content = U.marked_latex(val)

						// look for existing component
						let c = this.lesson_plan.find(x=>x.lc_uuid == lc.lc_uuid)

						// if component doesn't exist at all, create a new component
						if (empty(c)) {
							this.lesson_plan.push(new Lesson_Component_B({
								lc_uuid: lc.lc_uuid,
								lc_content: new_lc_content
							}))
							console.warn('added component ' + lc.lc_uuid)
							changed = true

						// else component does exist; update lc_content if it was previously empty
						} else {
							update_prop_value(c, 'lc_content', new_lc_content, `${lc.lc_uuid}.`)
						}
					}
				}
			}
		}

		// store case_resource_extensions, and note if changed (this could include, e.g., extra resources to be added to the lesson
		if (JSON.stringify(this.case_resource_extensions) != JSON.stringify(cfitem.extensions)) {
			this.case_resource_extensions = cfitem.extensions
			console.warn('case_resource_extensions changed')
			changed = true
		}

		return changed
	}

}
window.Lesson = Lesson

class Lesson_Component {
	constructor(data) {
		if (empty(data)) data = {}

		sdp(this, data, 'lc_uuid', U.new_uuid())
		sdp(this, data, 'lc_title', '')
		sdp(this, data, 'lc_weight', 0)
		sdp(this, data, 'lc_guidance', '')
		sdp(this, data, 'lc_default_content', '')

		// legacy: originally we coded lc_content as a string; convert that to the first lc_part
		if (data.lc_content) {
			data.lc_parts = [{lcp_teacher_content: data.lc_content}]
		}

		// the meat of the component is one or more Lesson_Component_Parts
		this.lc_parts = []
		// if lc_parts are included in data, bring them in
		if (data.lc_parts) {
			for (let lcp of data.lc_parts) {
				this.lc_parts.push(new Lesson_Component_Part(lcp))
			}
		} else {
			// otherwise add a single blank part
			this.lc_parts.push(new Lesson_Component_Part())
		}

		sdp(this, data, 'lc_showing', false)
	}

	copy_for_save(flag) {
		let o
		if (flag == 'master') {
			// for a master, we save everything except lc_parts and lc_showing
			o = $.extend(true, {}, this)
			delete o.lc_showing

		} else {
			// otherwise all we save is lc_parts
			o = {}
		}

		o.lc_parts = [];
		for (let c of this.lc_parts) {
			o.lc_parts.push(c.copy_for_save(flag))
		}

		return o
	}

	is_empty() {
		// if there are no parts to the component, empty
		if (this.lc_parts.length == 0) return true
		for (let part of this.lc_parts) {
			// if at least one part has teacher_content, not empty
			if (!empty(part.lcp_teacher_content)) return false
		}
		// if we get here, empty
		return true
	}
}
window.Lesson_Component = Lesson_Component

class Lesson_Component_Part {
	constructor(data) {
		if (empty(data)) data = {}

		sdp(this, data, 'lcp_title', '')
		sdp(this, data, 'lcp_teacher_content', '')

		sdp(this, data, 'lcp_showing', false)
		sdp(this, data, 'lcp_being_edited', false)
	}

	copy_for_save(flag) {
		let o = {}
		o.lcp_teacher_content = this.lcp_teacher_content
		o.lcp_title = this.lcp_title

		return o
	}

	has_content() {
		return !empty(this.lcp_teacher_content) || !empty(this.lcp_title)
	}
}
window.Lesson_Component_Part = Lesson_Component_Part

// Lesson components for type B
class Lesson_Component_B {
	constructor(data) {
		if (empty(data)) data = {}

		sdp(this, data, 'lc_uuid', '')
		sdp(this, data, 'lc_content', '')
		sdp(this, data, 'lc_unaltered_ai', false)

		// note that if we copy a lesson, lc_status might be empty for a component even though we do have content for it
		if (!empty(this.lc_content)) this.lc_status = 'complete'
		else sdp(this, data, 'lc_status', '', ['', 'skipped', 'complete'])

		sdp(this, data, 'lc_prompt_settings', {})
		sdp(this, data, 'lc_suggestions', [])	// this will hold the currently-showing suggestions, formatted for being added to the lesson
		sdp(this, data, 'lc_generated_suggestion_summaries', [])	// this will hold the summaries of all previously- suggested items
		sdp(this, data, 'lc_suggestion_length', 0)
		// sdp(this, data, 'lc_model_suggestions', [])	// not currently used
		sdp(this, data, 'lc_include_in_sparkl', false)
		sdp(this, data, 'lc_sparkl_model_response', '')
		sdp(this, data, 'lc_sparkl_exercise_id', '')
		sdp(this, data, 'lc_sparkl_query_ids', [])

		sdp(this, data, 'lc_asset_ids', [])

		// to be removed on save
		sdp(this, data, 'lc_showing', false)
		sdp(this, data, 'lc_open_for_editing', false)
		sdp(this, data, 'lc_option_other_chosen', false)
	}

	copy_for_save(flag) {
		let o
		if (flag == 'master') {
			// for a master, we (currently) save everything
			o = $.extend(true, {}, this)
		} else {
			o = {
				lc_uuid: this.lc_uuid,
				lc_content: this.lc_content,
				lc_unaltered_ai: this.lc_unaltered_ai,
				lc_status: this.lc_status,
				lc_prompt_settings: this.lc_prompt_settings,
				lc_suggestions: this.lc_suggestions,
				lc_generated_suggestion_summaries: this.lc_generated_suggestion_summaries,
				lc_suggestion_length: this.lc_suggestion_length,
				// lc_model_suggestions: this.lc_model_suggestions,
				lc_include_in_sparkl: this.lc_include_in_sparkl,
				lc_sparkl_model_response: this.lc_sparkl_model_response,
				lc_sparkl_exercise_id: this.lc_sparkl_exercise_id,
				lc_sparkl_query_ids: this.lc_sparkl_query_ids,
				lc_asset_ids: this.lc_asset_ids,
			}
			// copy through variant 'A' properties too if we have them
			if (this.lc_parts?.length > 0) {
				o.lc_title = this.lc_title
				o.lc_weight = this.lc_weight
				o.lc_guidance = this.lc_guidance
				o.lc_default_content = this.lc_default_content
				o.lc_parts = this.lc_parts
			}
		}
		return o
	}

	is_empty() {
		return empty(this.lc_content)
	}
}
window.Lesson_Component_B = Lesson_Component_B

// global fns for determining which lesson types should be available when

// these are the rules for whether or not to show the "toggler" between OG lessons and LPC lessons
U.show_lesson_variant_switcher = function(lesson_id, course_code) {
	let sc = vapp.site_config

	// if enable_llm_lesson_plans is 'no', we never allow toggling (though we would use the LPC if the lesson was created using the LPC)
	if (sc.enable_llm_lesson_plans == 'no' || sc.enable_llm_lesson_plans == 'always') return false

	// if sc.alba_collections is set, limit the enhanced lesson plan tool to these collections
	if (sc.alba_collections && sc.alba_collections.length > 0) {
		if (!course_code || !sc.alba_collections.includes(course_code)) return false
	}

	// if the lesson has already been saved (i.e. the lesson_id isn't 0)...
	if (lesson_id != 0) {
		// ... never show the switcher if allow_llm_lesson_conversion is no
		if (sc.allow_llm_lesson_conversion == 'no') return false
	}

	// if we get to here, we're in a context where the switcher is allowed
	return true
}

// these are the rules for which version to use for new lessons
U.use_llm_for_new_lessons = function(course_code) {
	let sc = vapp.site_config

	// if enable_llm_lesson_plans is 'always', always use the llm for new lessons
	if (sc.enable_llm_lesson_plans == 'always') return true

	// if enable_llm_lesson_plans is 'no', never use the llm for new lessons
	if (sc.enable_llm_lesson_plans == 'no') return false
	
	// if sc.alba_collections is set, limit the enhanced lesson plan tool to these collections
	if (sc.alba_collections && sc.alba_collections.length > 0) {
		if (!course_code || !sc.alba_collections.includes(course_code)) return false
	}

	// if we get to here, use the store.state.lst setting to determine which version to use
	return vapp.$store.state.lst.use_llm_for_new_lessons 
}

// use this to determine which variant to show for a particular lesson
// use_enhanced_lesson_editor() { return this.lesson.lp_variant=='B' },


// note that lesson reports are stored in the db as resources
class Lesson_Report {
	constructor(data) {
		if (empty(data)) data = {}

		if (data.lesson_report_id == 'new') this.lesson_report_id = U.new_uuid()
		else if (!empty(data.lesson_report_id)) this.lesson_report_id = data.lesson_report_id
		else {
			console.warn('Lesson report created with no lesson_report_id')
			// this.lesson_report_id = U.new_uuid()
		}

		sdp(this, data, 'lesson_ids', [])
		sdp(this, data, 'collaborator_user_ids', [])
		sdp(this, data, 'reviewer_user_ids', [])
		sdp(this, data, 'owner_user_id', 0)
		// 'WLP' is used as the 'empty' value for report_title
		sdp(this, data, 'report_title', 'WLP')
		sdp(this, data, 'description', this.report_title)	// description is "shorthand" text for the report
		sdp(this, data, 'prework', {})	// indexed by user_id; values are strings
		sdp(this, data, 'feedback', {})	// indexed by user_id; values are strings
		
		this.reviews = {}	// indexed by user_id; values are {approved: true}
		if (data.reviews) for (let user_id in data.reviews) {
			let o = {}
			sdp(o, data.reviews[user_id], 'approved', false)
			this.reviews[user_id] = o
		}

		// convert dates from mysql to timestamp if necessary
		let tzo = new Date().getTimezoneOffset()*60*1000	// dates come in GMT; convert to local

		if (!isNaN(data.created_at*1)) this.created_at = data.created_at*1
		else this.created_at = (empty(data.created_at)) ? 0 : (date.parse(data.created_at, 'YYYY-MM-DD HH:mm:ss').getTime() - tzo) / 1000
		
		// these are removed before saving
		sdp(this, data, 'fully_loaded', (!empty(data.owner_user_id)))	// if we didn't get an owner_user_id, we didn't fully load the report
		sdp(this, data, 'viewed', false)
	}

	copy_for_save(flag) {
		let o = $.extend(true, {}, this)

		delete o.fully_loaded
		delete o.viewed
		delete o.created_at

		return o
	}
}
window.Lesson_Report = Lesson_Report


window.variant_b_lesson_master_data = {
	"lesson_id": 9,	// originally 14613
	"lesson_master_id": 9,
	"lesson_template_id": 0,
	"lesson_title": "ALBA Lesson Master",
	"lp_variant": "B",
	"lesson_plan": [
		{
			"lc_uuid": "overview",
			"lc_title": "Overview",
			"lc_title_plural": "Overview",
			"lc_icon": "fas fa-image-landscape",
			"lc_color": "pink",
			"lc_guidance": "<p>Provide an overview of the lesson.</p>",
			"lc_text_format": "paragraphs",
			"lc_suggestible_criteria": [],
			"lc_offer_to_include_in_sparkl": false,
			// note that lc_case_field should be specified IFF the component can have data imported from a CASE CFItem's extensions
			"lc_case_field": "description",

		},
		// Generate ${n_suggestions} suggested...
		{
			"lc_uuid": "topics",
			"lc_title": "Objectives / Targets",
			"lc_title_plural": "Objectives / Targets",
			"lc_icon": "fas fa-list-check",
			"lc_color": "pink",
			"lc_guidance": "<p>Specify the objectives and/or targets you will cover in this lesson.</p>",
			"lc_text_format": "list",
			"lc_suggestible_criteria": ["standards", "topics", "opener", "closer"],
			"lc_not_suggestible_msg": "To make Objectives / Targets suggestions, you must select one or more Standards, enter one or more objectives or targets yourself, or write an Opening Question or a Closing Question for the lesson.",

			"lc_gpt_messages_start": "The teacher will be covering the following **learning objectives** in the lesson:\n$format_for_gpt_message",

			"lc_gpt_messages_prompt": "**learning objectives** the teacher may want to cover in the lesson",
			// "lc_gpt_messages_prompt_alt": "The teacher will be covering the following key concepts in the lesson: $format_for_gpt_message.\n\nGenerate $suggestions_to_request *different* suggestions for **key concepts** the teacher may want to cover in the lesson.",

			"lc_offer_to_include_in_sparkl": true,
			"lc_sparkl_include_method": "student_instructions",
			"lc_sparkl_include_help_text": "The Objectives / Targets you have entered will be included in the Student Instructions of the activity.",
			"lc_case_field": "learningTarget",	
			// this is likely to need to be changed in some schemes, depending on how any given subject area defines what to use for this component
			// e.g. it might be 'topics' or 'objective'

		},
		// materials and teacherprep moved in the list 9/26/2025
		{
			"lc_uuid": "materials",
			"lc_title": "Materials & Resources",
			"lc_title_plural": "Materials & Resources",
			"lc_icon": "fas fa-shapes",
			"lc_color": "pink",
			"lc_guidance": "<p>List necessary non-digital materials for the lesson, and link to digital materials below.</p>",
			"lc_text_format": "list",
			"lc_suggestible_criteria": [],
			"lc_offer_to_include_in_sparkl": false,
			"lc_case_field": "materials",

		},
		{
			"lc_uuid": "teacherprep",
			"lc_title": "Background / Preparation",
			"lc_title_plural": "Background / Preparation",
			"lc_icon": "fas fa-compass",
			"lc_color": "pink",
			"lc_guidance": "<p>Describe any necessary Background and Preparation teachers need before conducting this lesson.</p>",
			"lc_text_format": "paragraphs",
			"lc_suggestible_criteria": [],
			"lc_offer_to_include_in_sparkl": false,

		},
		{
			"lc_uuid": "opener",
			"lc_title": "Opening Question",
			"lc_title_plural": "Opening Questions",
			"lc_icon": "fas fa-circle-question",
			"lc_color": "pink",
			"lc_guidance": "<p>Frame your lesson around an opening question for your students to answer.</p>",
			"lc_text_format": "line",
			"lc_no_summary_in_suggestion": true,
			"lc_suggestible_criteria": ["standards", "topics", "closer"],
			"lc_not_suggestible_msg": "To make Opening Question suggestions, you must select one or more Standards, enter one or more Objectives / Targets, or write a Closing Question for the lesson.",

			"lc_gpt_messages_start": "The teacher will be using the following **opening question** in the lesson:\n$format_for_gpt_message",

			"lc_gpt_messages_prompt": "**questions** the teacher may want to ask their students at the **start** of the lesson, making sure to reference real-world examples in each question",
			
			"lc_offer_to_include_in_sparkl": true,
			"lc_sparkl_include_method": "cr_query",
			"lc_sparkl_include_help_text": "A constructed response query with your question text will be added.",
			// "lc_sparkl_include_help_text": "A constructed response query with your question text will be added. If you specify a model response, students will see the model after entering their answer, and $sparkl_app_name-BOT will compare each student’s answer to the model to assist with grading.",
			// "lc_gpt_model_prompt": "\nuser: Compose a model response, limited to 80 words or fewer, that a student $grade_phrase might give to the following question: $format_for_gpt_message"

		},
		{
			"lc_uuid": "review",
			"lc_title": "Prior Knowledge",
			"lc_title_plural": "Prior Knowledge",
			"lc_icon": "fas fa-clipboard-list",
			"lc_color": "pink",
			"lc_guidance": "<p>What prior knowledge will students need prior to the lesson?</p>",
			"lc_text_format": "list",
			"lc_suggestible_criteria": ["standards", "topics", "review", "opener", "closer"],
			"lc_not_suggestible_msg": "To make Prior Knowledge suggestions, you must select one or more Standards, enter one or more Lessons / Objectives, enter one or more Prior Knowledge statements yourself, or write an Opening Question or a Closing Question for the lesson.",

			"lc_gpt_messages_prompt": "**key concepts and prior knowledge** that students **must already understand** before starting the lesson",
			// "lc_gpt_messages_prompt_alt": "\nuser: I have determined that students must already understand the following key concepts before starting the lesson: $format_for_gpt_message.\nuser: Please create a list of up to $suggestions_to_request additional key concepts students must already understand before starting the lesson.",

			"lc_offer_to_include_in_sparkl": true,
			"lc_sparkl_include_method": "interactive_reading",
			"lc_sparkl_include_help_text": "The Prior Knowledge statements you have entered will be included as an Interactive Reading exercise.",

		},

		/////////////////////////////////////////////////
		// Generic "Instructional Sequence" component -- could also be called "Lesson Roadmap", "Lesson Flow", etc.
		{
			"lc_uuid": "lesson_flow",
			"lc_title": "Instructional Sequence",
			"lc_icon": "fas fa-person-chalkboard",
			'lc_guidance': '<p><b>This is where the rubber meets the road!</b><br>Describe the instructional roadmap for your lesson plan, including:<ul><li>How you will engage your students (e.g. with an opening question)</li><li>Direct instruction you will deliver (“I do”)</li><li>Guided practice you will employ with your students (“We do”)</li><li>Independent practice activities you will have your students complete (“You do”)</li></ul></p>',
			'lc_text_format': 'paragraphs',
			'lc_suggestible_criteria': ['standards', 'topics', 'opener', 'closer', 'lesson_flow'],
			'lc_not_suggestible_msg': 'To make Lesson Roadmap suggestions, you must select one or more Standards, enter one or more Objectives/Understandings, or start writing out the Lesson Roadmap yourself.',
			'lc_prompt_height': 300,
			'lc_gpt_messages_prompt': 'elements for the instructional flow of the teacher’s lesson plan',
			'lc_gpt_add_time_to_prompt': true,
		},

		/////////////////////////////////////////////////
		// Gradual Release: Teacher Instruction / Guided Practice / Independent Practice
		{
			"lc_uuid": "ido",
			"lc_title": "Teacher Instruction",
			"lc_title_plural": "Teacher Instruction Ideas",
			"lc_icon": "fas fa-person-chalkboard",
			"lc_color": "blue",
			"lc_guidance": "<p>Plan the direct instruction you will do in the lesson.</p>",
			"lc_text_format": "paragraphs",
			"lc_suggestible_criteria": ["standards", "topics", "opener", "ido", "closer"],
			"lc_use_supp_info_in_prompt": true,
			"lc_not_suggestible_msg": "To make Teacher Instruction suggestions, you must select one or more Standards, enter one or more Objectives / Targets, start writing Teacher Instruction plans yourself, or write an Opening Question or a Closing Question for the lesson.",

			"lc_gpt_messages_prompt": "actions the teacher can take during **direct instruction**. Focus on teacher-led explanations, demonstrations, modeling, or examples that introduce and clarify the lesson’s key concepts",
			// "lc_gpt_messages_prompt_alt": "\nuser: I will be doing the following direct teacher instruction in the lesson: $format_for_gpt_message.\nuser: Please suggest up to $suggestions_to_request additional ideas for direct teacher instruction.",

			"lc_gpt_add_time_to_prompt": true,
			"lc_offer_to_include_in_sparkl": true,
			"lc_sparkl_include_method": "interactive_reading",
			"lc_sparkl_placeholder_text": "<p><i>Replace this text with any passage or excerpt that would be helpful for students to engage with before, during, or after your direct instruction about the topic(s) of this activity. Use the auto-blanks option or the toolbar above to choose specific words for students to interact with to preserve their memory trace of this new information. See the activity gallery for ideas and options <a href=\"https://docs.google.com/presentation/d/1PVtSrqmrmZqiEiIZx6585pD9340ap0Qt7IxkBr1j-70/edit#slide=id.g25af3f611c6_0_0\" target=\"_blank\">here.</i></p>",
			"lc_sparkl_include_help_text": "A placeholder Interactive Reading exercise will be added to the $sparkl_app_name activity."

		},
		{
			"lc_uuid": "wedo",
			"lc_title": "Guided Practice",
			"lc_title_plural": "Guided Practice Ideas",
			"lc_icon": "fas fa-person-walking",
			"lc_color": "blue",
			"lc_guidance": "<p>Plan the guided practice you will do with your students.</p>",
			"lc_text_format": "paragraphs",
			"lc_suggestible_criteria": ["standards", "topics", "opener", "wedo", "closer"],
			"lc_use_supp_info_in_prompt": true,

			"lc_not_suggestible_msg": "To make Guided Practice suggestions, you must select one or more Standards, enter one or more Objectives / Targets, start writing Guided Practice plans yourself, or write an Opening Question or a Closing Question for the lesson.",

			"lc_gpt_messages_prompt": "ideas for **guided practice activities**. Focus on things the teacher and students can work through together to help students apply the key concepts with teacher support",
			// "lc_gpt_messages_prompt_alt": "The teacher will be doing the following guided practice with their students in the lesson: $format_for_gpt_message\n\nGenerate $suggestions_to_request *different* suggestions for **guided practice** (“we do”) activities the teacher can do with their students.",

			"lc_gpt_add_time_to_prompt": true,
			"lc_offer_to_include_in_sparkl": true,
			"lc_sparkl_include_method": "scaffolded_response",
			"lc_sparkl_placeholder_text": "<p><i>Replace this text with a prompt or question for students to think about related to the topic(s) of this activity. Use the space below the prompt to construct a model response to your prompt or question and choose specific words for students to interact with to check their understanding and reinforce the main take aways for the topic(s) of this activity. See the activity gallery for ideas and options <a href=\"https://docs.google.com/presentation/d/1PVtSrqmrmZqiEiIZx6585pD9340ap0Qt7IxkBr1j-70/edit#slide=id.g25af3f611c6_0_144\" target=\"_blank\">here.</i></p>",
			"lc_sparkl_include_help_text": "A placeholder Scaffolded Response exercise will be added to the $sparkl_app_name activity."

		},
		{
			"lc_uuid": "youdo",
			"lc_title": "Independent Practice",
			"lc_title_plural": "Independent Practice Ideas",
			"lc_icon": "fas fa-person-running",
			"lc_color": "blue",
			"lc_guidance": "<p>Plan the independent practice you will have your students do.</p>",
			"lc_text_format": "paragraphs",
			"lc_suggestible_criteria": ["standards", "topics", "opener", "youdo", "closer"],
			"lc_use_supp_info_in_prompt": true,
			"lc_not_suggestible_msg": "To make Independent Practice suggestions, you must select one or more Standards, enter one or more Objectives / Targets, start writing Independent Practice plans yourself, or write an Opening Question or a Closing Question for the lesson.",

			"lc_gpt_messages_prompt": "ideas for **independent practice activities**. Focus on tasks students can complete on their own to apply the key concepts from the lesson without direct teacher assistance",
			// "lc_gpt_messages_prompt_alt": "\nuser: I will be asking my students to do the following independent practice activities in the lesson: $format_for_gpt_message.\nuser: Please suggest up to $suggestions_to_request additional ideas for student independent practice activities.",

			"lc_gpt_add_time_to_prompt": true,
			"lc_offer_to_include_in_sparkl": true,
			"lc_sparkl_include_method": "basic_content",
			"lc_sparkl_placeholder_text": "<p><i>Replace this text with an image to label, a table to fill in, a list of steps with blanks, or any other content that can give learners the chance to demonstrate what they know about the topic(s) and get immediate feedback. See the activity gallery for ideas and options <a href=\"https://docs.google.com/presentation/d/1PVtSrqmrmZqiEiIZx6585pD9340ap0Qt7IxkBr1j-70/edit#slide=id.g25c43433b1f_0_0\" target=\"_blank\">here.</i></p>",
			"lc_sparkl_include_help_text": "A placeholder Freeform exercise will be added to the $sparkl_app_name activity.",

		},

		/////////////////////////////////////////////////
		// 5E Instructional Model: Engage / Explore / Explain / Elaborate / Evaluate
		// map engage on to wedo
		// map explore on to youdo
		// map explain on to ido
		{
			"lc_uuid": "5e_elaborate",
			"lc_title": "Elaborate",
			"lc_title_plural": "Elaborate Ideas",
			"lc_icon": "fas fa-chart-network",
			"lc_color": "blue",
			"lc_guidance": "<p>How will you <i>Elaborate</i> the lesson topics with your students?</p>",
			"lc_text_format": "paragraphs",
			"lc_suggestible_criteria": ["standards", "topics", "opener", "5e_elaborate", "closer"],
			"lc_use_supp_info_in_prompt": true,
			"lc_not_suggestible_msg": "To make Elaborate suggestions, you must select one or more Standards, enter one or more Objectives / Targets, start writing Elaborate plans yourself, or write an Opening Question or a Closing Question for the lesson.",

			"lc_gpt_messages_prompt": "ideas for how the teacher can **elaborate** on the lesson’s key concepts with students. Focus on ways to deepen understanding, make real-world connections, or extend the concepts beyond the initial instruction",
			// "lc_gpt_messages_prompt_alt": "\nuser: I already plan to be using the following ideas to elaborate the topics: $format_for_gpt_message.\nuser: Please suggest up to $suggestions_to_request additional ideas for how I can *elaborate* the lesson topics with my students.",

			"lc_gpt_add_time_to_prompt": true,
			"lc_offer_to_include_in_sparkl": true,
			"lc_sparkl_include_method": "interactive_reading",
			"lc_sparkl_placeholder_text": "<p><i>Replace this text with a passage, video, or interactive that will help <i>elaborate</i> the lesson material with your students. Use the auto-blanks option or the toolbar above to choose specific words for students to interact with to preserve their memory trace of this new information. See the activity gallery for ideas and options <a href=\"https://docs.google.com/presentation/d/1PVtSrqmrmZqiEiIZx6585pD9340ap0Qt7IxkBr1j-70/edit#slide=id.g25af3f611c6_0_0\" target=\"_blank\">here.</i></p>",
			"lc_sparkl_include_help_text": "A placeholder Interactive Reading exercise will be added to the $sparkl_app_name activity."

		},


		/////////////////////////////////////////////////
		{
			"lc_uuid": "vocabulary",
			"lc_title": "Vocabulary Review",
			"lc_title_plural": "Vocabulary Review Terms",
			"lc_icon": "fas fa-book",
			"lc_color": "green",
			"lc_guidance": "<p>Make a list of vocabulary words for the lesson, along with definitions for each word.</p>",
			"lc_suppress_detail_prompt": true,
			"lc_text_format": "list_plain",		// this will make it so we don't process markdown
			"lc_suggestible_criteria": ["standards", "topics", "opener"],
			"lc_not_suggestible_msg": "To make Vocabulary suggestions, you must select one or more Standards, enter one or more Objectives / Targets, or write an Opening Question for the lesson.",

			"lc_gpt_messages_prompt": "**vocabulary terms** the teacher may want to review with their students for the lesson, along with a **definition** for each term",

			"lc_offer_to_include_in_sparkl": true,
			"lc_sparkl_include_method": "flashcards",
			"lc_sparkl_include_help_text": "A Flashcard exercise will be added with your specified Vocabulary Review words and definitions.",
			"lc_case_field": "vocabulary",

		},
		{
			"lc_uuid": "closer",
			"lc_title": "Closing Question",
			"lc_title_plural": "Closing Questions",
			"lc_icon": "fas fa-circle-question",
			"lc_color": "green",
			"lc_guidance": "<p>Write a &ldquo;capstone&rdquo; Closing Question for your students to answer at the end of the lesson.</p>",
			"lc_text_format": "line",
			"lc_no_summary_in_suggestion": true,
			"lc_suggestible_criteria": ["standards", "topics", "opener"],
			"lc_not_suggestible_msg": "To make Closing Question suggestions, you must select one or more Standards, enter one or more Objectives / Targets, or write an Opening Question for the lesson.",

			"lc_gpt_messages_start": "The teacher will be using the following **closing question** in the lesson:\n$format_for_gpt_message",

			"lc_gpt_messages_prompt": "**“capstone” questions** the teacher may want to ask their students at the **end** of the lesson",

			"lc_offer_to_include_in_sparkl": true,
			"lc_sparkl_include_method": "cr_query",
			"lc_sparkl_include_help_text": "A constructed response query with your question text will be added.",
			// "lc_sparkl_include_help_text": "A constructed response query with your question text will be added. If you specify a model response, students will see the model after entering their answer, and $sparkl_app_name-BOT will compare each student’s answer to the model to assist with grading.",
			// "lc_gpt_model_prompt": "\nuser: Compose a model response, limited to 80 words or fewer, that a student $grade_phrase might give to the following question: $format_for_gpt_message"

		},
		{
			"lc_uuid": "miscon",
			"lc_title": "Common Misconceptions",
			"lc_title_plural": "Common Misconceptions",
			"lc_icon": "fas fa-triangle-exclamation",
			"lc_color": "green",
			"lc_guidance": "<p>What common misconceptions might your students have about the lesson content?</p>",
			"lc_text_format": "list",
			"lc_suggestible_criteria": ["standards", "topics", "opener", "closer"],
			"lc_not_suggestible_msg": "To make Misconception suggestions, you must select one or more Standards, enter one or more Objectives / Targets, or write an Opening Question or a Closing Question for the lesson.",

			"lc_gpt_messages_prompt": "common **misconceptions** that students may have about the lesson content",

			"lc_offer_to_include_in_sparkl": false,

		},
		{
			"lc_uuid": "remediation",
			"lc_title": "Ideas for Intervention",
			"lc_title_plural": "Ideas for Intervention",
			"lc_icon": "fas fa-toolbox",	// bandage / turtle
			"lc_color": "lime",
			"lc_guidance": "<p>How can you accommodate students who need additional intervention to help with learning?</p>",
			"lc_text_format": "list",
			"lc_suggestible_criteria": ["standards", "topics", "opener", "closer"],
			"lc_not_suggestible_msg": "To make Intervention suggestions, you must select one or more Standards, enter one or more Objectives / Targets, or write an Opening Question or a Closing Question for the lesson.",

			"lc_gpt_messages_prompt": "ideas for providing additional **remediation** to students who need extra support. Focus on strategies that help students revisit key concepts, build missing prerequisite skills, or receive targeted clarification",

			"lc_offer_to_include_in_sparkl": false,

		},
		{
			"lc_uuid": "acceleration",
			"lc_title": "Ideas for Acceleration",
			"lc_title_plural": "Ideas for Acceleration",
			"lc_icon": "fas fa-rabbit-running",
			"lc_color": "lime",
			"lc_guidance": "<p>How can you challenge students who demonstrated mastery of the lesson concepts?</p>",
			"lc_text_format": "list",
			"lc_suggestible_criteria": ["standards", "topics", "opener", "closer"],
			"lc_not_suggestible_msg": "To make Acceleration suggestions, you must select one or more Standards, enter one or more Objectives / Targets, or write an Opening Question or a Closing Question for the lesson.",

			"lc_gpt_messages_prompt": "ideas for extending the lesson for **accelerated students**. Focus on activities that deepen, enrich, or extend the key concepts beyond the core lesson",

			"lc_offer_to_include_in_sparkl": false,

		},
		{
			'lc_uuid': 'differentiation',
			'lc_title': 'Common Accommodations',
			'lc_icon': 'fas fa-toolbox', 
			'lc_guidance': '<p>How can you differentiate instruction for students who need extra help or extra challenges?</p>',
			"lc_suggestible_criteria": ["standards", "topics", "opener", "closer"],
			"lc_gpt_messages_prompt": "ideas for extending the lesson for **accelerated students** and **students who need extra support**.",
			'lc_text_format': 'list',
			"lc_case_field": "differentiation",
			// instances (e.g. inspire) with legacy (variant A) plans may need this
			'lc_convertible_to': ['legacy_differentiation'],

		},
		{
			"lc_uuid": "assessment",
			"lc_title": "Assessment Strategies",
			"lc_title_plural": "Assessment Strategies",
			"lc_icon": "fas fa-clipboard-question",
			"lc_color": "lime",
			"lc_guidance": "<p>How will you assess student learning for the lesson’s content?<br><b>NOTE:</b> You can create specific questions and interactive activities for students in an accompanying $sparkl_app_name activity; see the “Materials” section.</p>",
			"lc_text_format": "paragraphs",
			"lc_suggestible_criteria": ["standards", "topics", "opener", "closer"],
			"lc_not_suggestible_msg": "To make $lc_title suggestions, you must select one or more Standards, enter one or more Objectives / Targets, or write an Opening Question or a Closing Question for the lesson.",

			"lc_gpt_messages_prompt": "*general recommendations* -- *NOT SPECIFIC QUESTIONS* -- for how to assess whether students have mastered the material in the lesson",

			"lc_offer_to_include_in_sparkl": false,

		},
		{
			"lc_uuid": "reflection",
			"lc_title": "Reflection Notes",
			"lc_title_plural": "Reflection Notes",
			"lc_icon": "fas fa-face-thinking",
			"lc_color": "lime",
			"lc_guidance": "<p>Reflect on how the lesson went by reviewing evidence of students’ progress toward the learning objective/target, noting who met it, and planning any needed adjustments to instruction.</p>",
			"lc_text_format": "paragraphs",
			"lc_suggestible_criteria": [],
			"lc_offer_to_include_in_sparkl": false,

		},
		{
			"lc_uuid": "duration",
			"lc_title": "Duration",
			"lc_icon": "fas fa-timer",
			"lc_guidance": "<p>Expected class time needed for lesson completion.</p>",
			"lc_text_format": "line",
			"lc_suggestible_criteria": [],
			"lc_offer_to_include_in_sparkl": false,
			"lc_case_field": "duration",

		},
		{
			"lc_uuid": "essential_question",
			"lc_title": "Essential Questions",
			"lc_icon": "fas fa-seal-question",
			"lc_color": "lime",
			"lc_guidance": "<p>Essential question(s) to be asked and addressed in the lesson.</p>",
			"lc_text_format": "line",
			"lc_suggestible_criteria": [],
			"lc_offer_to_include_in_sparkl": false,
			"lc_case_field": "essentialQuestion",
		},

		/////////////////////////////////////////////////
		// Cureum instances that need support for legacy lesson plans should include components like the following (see inspire)
		// Components for legacy lesson plan conversion -- no suggestions for these
		// {
		// 	"lc_uuid": "legacy_plan",
		// 	"lc_title": "Learning Plan",
		// 	"lc_icon": "fas fa-person-chalkboard",
		// 	'lc_guidance': '<p>Describe the instructional roadmap for your lesson plan</p>',
		// 	'lc_prompt_height': 300,
		// 	'lc_legacy_component_index': 0,
		// 	'lc_convertible_to': ['lesson_flow', 'ido'],
		// },
		// {
		// 	"lc_uuid": "legacy_differentiation",
		// 	"lc_title": "Ideas for Differentiation",
		// 	"lc_icon": "fas fa-toolbox",
		// 	'lc_guidance': '<p>How can you differentiate instruction for students who need extra help or extra challenges?</p>',
		// 	'lc_prompt_height': 100,
		// 	'lc_legacy_component_index': 1,
		// 	'lc_convertible_to': ['differentiation', 'remediation'],
		// },
		/////////////////////////////////////////////////

	],
	// specify things here we want to be able to overwrite in instances for standards; but we don't want it to be part of the lesson plan
	"standards_component": {
		"lc_uuid": "standards",
		"lc_title": "Learning Standards",
		"lc_title_plural": "Learning Standards",
		"lc_icon": "fas fa-map",
		"lc_color": "amber",
		"lc_guidance": "<p>Choose the learning standard(s) you will cover in this lesson.</p>",
		"lc_offer_to_include_in_sparkl": false,
	},

	"lp_component_schemes": {
		/////////////////////////////////////////////////
		// Cureum instances that need support for legacy lesson plans should include legacy schemes such as the following (see inspire)
		// "legacy_5": {
		// 	'variant_a_master_id': 5,
		// 	"label": "Legacy Lesson Plan",
		// 	"groups": [
		// 		{ 
		// 			"lc_uuid": "group_instructionalsequence",
		// 			"title": "Instructional Sequence", 
		// 			"color": "blue",
		// 			"group_components": ["legacy_plan" ], 
		// 		},
		// 		{ 
		// 			"lc_uuid": "group_studentsupport",
		// 			"title": "Student Support", 
		// 			"color": "lime",
		// 			"group_components": ["legacy_differentiation", ], 
		// 		},
		// 		{ 
		// 			"lc_uuid": "group_prework",
		// 			"title": "Lesson Setup", 
		// 			"color": "pink",
		// 			"group_components": ["materials" ], 
		// 		},
		// 	],
		// },
		/////////////////////////////////////////////////

		"gradual_release": {
			"label": "Gradual Release",
			"groups": [
				{ 
					"lc_uuid": "group_prework",
					"title": "Lesson Setup", 
					"color": "pink",
					"group_components": ["overview", "topics", "teacherprep", "materials", "review", "opener",], 
				},
				{ 
					"lc_uuid": "group_instructionalsequence",
					"title": "Instructional Sequence", 
					"color": "blue",
					"group_components": ["ido", "wedo", "youdo", "assessment" ], 
				},
				{ 
					"lc_uuid": "group_review",
					"title": "Review", 
					"color": "green",
					"group_components": ["closer", "vocabulary", "miscon", ], 
				},
				{ 
					"lc_uuid": "group_studentsupport",
					"title": "Student Support", 
					"color": "lime",
					"group_components": ["remediation", "acceleration", ], 
				},
			],
			"edit_order": [
				"topics", "review", "opener",
				"ido", "wedo", "youdo", "assessment", 
				"closer", "vocabulary", "miscon", 
				"remediation", "acceleration", 
				"materials", "teacherprep", "overview", 
				"topics",	// this allows the code to automatically "re-cycle" when it gets to the last item
			],
		},
		"5e": {
			"label": "5E Instructional Model",
			"groups": [
				{ 
					"lc_uuid": "group_prework",
					"title": "Lesson Setup", 
					"color": "pink",
					"group_components": ["overview", "topics", "materials", "teacherprep", "review", "opener",], 
				},
				{ 
					"lc_uuid": "group_instructionalsequence",
					"title": "Instructional Sequence", 
					"color": "blue",
					"group_components": ["wedo", "youdo", "ido", "5e_elaborate", "assessment" ], 
				},
				{ 
					"lc_uuid": "group_review",
					"title": "Review", 
					"color": "green",
					"group_components": ["closer", "vocabulary", "miscon", ], 
				},
				{ 
					"lc_uuid": "group_studentsupport",
					"title": "Student Support", 
					"color": "lime",
					"group_components": ["remediation", "acceleration", ], 
				},
			],
			"edit_order": [
				"topics", "review", "opener",
				"wedo", "youdo", "ido", "5e_elaborate", "assessment", 
				"closer", "vocabulary", "miscon", 
				"remediation", "acceleration", 
				"materials", "teacherprep", "overview", 
				"topics",	// this allows the code to automatically "re-cycle" when it gets to the last item
			],
			"alt_component_data": {
				// map engage on to wedo
				"wedo": {
					"lc_title": "Engage",
					"lc_title_plural": "Engage Ideas",
					"lc_guidance": "<p>How will you <i>Engage</i> your students?</p>",
					"lc_not_suggestible_msg": "To make Engage suggestions, you must select one or more Standards, enter one or more Objectives / Targets, start writing Engage plans yourself, or write an Opening Question or a Closing Question for the lesson.",
					
					"lc_gpt_messages_prompt": "ideas for how the teacher can *engage* their students in the lesson topics",
					// "lc_gpt_messages_prompt_alt": "\nuser: I already plan to be using the following ideas to engage my students: $format_for_gpt_message.\nuser: Please suggest up to $suggestions_to_request additional ideas for how I can *engage* my students in the lesson topics.",

					"lc_sparkl_placeholder_text": "<p><i>Replace this text with a passage, video, or interactive that will help <i>engage</i> students with the lesson material. Use the auto-blanks option or the toolbar above to choose specific words for students to interact with to preserve their memory trace of this new information. See the activity gallery for ideas and options <a href=\"https://docs.google.com/presentation/d/1PVtSrqmrmZqiEiIZx6585pD9340ap0Qt7IxkBr1j-70/edit#slide=id.g25af3f611c6_0_0\" target=\"_blank\">here.</i></p>",
				},

				// map explore on to youdo
				"youdo": {
					"lc_title": "Explore",
					"lc_title_plural": "Explore Ideas",
					"lc_guidance": "<p>How will your students <i>Explore</i> the lesson topics?</p>",
					"lc_not_suggestible_msg": "To make Explore suggestions, you must select one or more Standards, enter one or more Objectives / Targets, start writing Explore plans yourself, or write an Opening Question or a Closing Question for the lesson.",

					"lc_gpt_messages_prompt": "ideas for how students can *explore* the lesson topics",
					// "lc_gpt_messages_prompt_alt": "\nuser: I already plan to be using the following exploration ideas: $format_for_gpt_message.\nuser: Please suggest up to $suggestions_to_request additional ideas for how I my students can *explore* the lesson topics.",

					"lc_sparkl_placeholder_text": "<p><i>Replace this text with a passage, video, or interactive that will help students <i>explore</i> the lesson material. See the activity gallery for ideas and options <a href=\"https://docs.google.com/presentation/d/1PVtSrqmrmZqiEiIZx6585pD9340ap0Qt7IxkBr1j-70/edit#slide=id.g25af3f611c6_0_0\" target=\"_blank\">here.</i></p>",
				},

				// map explain on to ido
				"ido": {
					"lc_title": "Explain",
					"lc_title_plural": "Explain Ideas",
					"lc_guidance": "<p>How will you <i>Explain</i> lesson topics to your students?</p>",
					"lc_not_suggestible_msg": "To make Explain suggestions, you must select one or more Standards, enter one or more Objectives / Targets, start writing Explain plans yourself, or write an Opening Question or a Closing Question for the lesson.",

					"lc_gpt_messages_prompt": "ideas for how the teacher can *explain* the lesson topics to their students",
					// "lc_gpt_messages_prompt_alt": "\nuser: I already plan to be using the following ideas to explain the topics: $format_for_gpt_message.\nuser: Please suggest up to $suggestions_to_request additional ideas for how I can *explain* the lesson topics to my students.",

					"lc_sparkl_placeholder_text": "<p><i>Replace this text with a passage, video, or interactive that will help <i>explain</i> the lesson material to your students. Use the auto-blanks option or the toolbar above to choose specific words for students to interact with to preserve their memory trace of this new information. See the activity gallery for ideas and options <a href=\"https://docs.google.com/presentation/d/1PVtSrqmrmZqiEiIZx6585pD9340ap0Qt7IxkBr1j-70/edit#slide=id.g25af3f611c6_0_0\" target=\"_blank\">here.</i></p>",

				},

				// map evaluate on to assessment
				"assessment": {
					"lc_title": "Evaluate",
					"lc_title_plural": "Evaluate Ideas",
					"lc_guidance": "<p>How will you <i>Evaluate</i> your students’ learning?</p>",
				},
			},
		},
	}
}

/*
To change in config.php:

	'variant_b_lesson_plan_edits' => [
		// change 'Intervention' to 'Remediation' in the lesson master
		[
			"lc_uuid" => "remediation",
			"lc_title" => "Ideas for Remediation",
			"lc_title_plural" => "Ideas for Remediation",
			"lc_guidance" => "<p>How can you accommodate students who need remediation to help with learning?</p>",
		],
	],

	// these are samples; they don't actually change anything
	'variant_b_lesson_plan_scheme_edits' => [
		'5e' => [
			'groups' => [
				[ 
					'lc_uuid' => 'group_prework',
					'title' => 'Lesson Setup', 
				],
			],
			'alt_component_data' => [
				'wedo' => [
					'lc_title' => 'Engage'
				]
			]
		]
	],

*/